import {
    ClientMode,
    EditorLayersMode,
    EditorTool,
    EditorToolType,
    ErrorType,
    ExtendedEditorToolDefinition,
    ExternalLayerImported,
    GroupFeaturePointer,
    LayerId,
    MapEditorActions,
} from '@mc/react-mapio';
import {Context, createContext, Dispatch, MutableRefObject, SetStateAction, useContext, useMemo, useState} from 'react';
import {CatalogItemVO, LayerVO, TopicVO} from './api';
import {getParentLayerId} from './helpers/LayerConfigUtils';

export type MapioContextValue = {
    topicId: string;
    topic: TopicVO;
    context: string;
    lang: string;
    instanceId: string;
    clientMode: ClientMode;
    errors: ErrorType[];
    externalLayers: ExternalLayerImported[];
    externalLayersCatalog?: CatalogItemVO[];
    isIframe: boolean;
    layersConfig?: {[key: string]: LayerVO};
    currentTools?: (EditorTool | ExtendedEditorToolDefinition)[];
    mapRef: MutableRefObject<MapEditorActions> | null;
    writableLayers: LayerId[];
    activeTool: EditorToolType | null;
    insertionLayerId?: LayerId;
    selectedDetailFeature: GroupFeaturePointer[];
    activeLayers: LayerId[];
    disabled: boolean;
    layersMode: EditorLayersMode;
    selectedLayerId: LayerId | null;
    layerSettingsEnabled: boolean;
    attributeTableDiscriminatedChildId: LayerId | null;
    isAttributeTableOpen: boolean;
    /** Otevřená klientská atributová tabulka pro externí (připojenou / exportovanou) vrstvu. */
    isClientAttributeTableOpen: boolean;
    clientAttributeTableLayerId: LayerId | null;
};

export const defaultMapioContextValue: MapioContextValue = {
    topicId: null, // nechceme tu žádný výchozí, ten určíme až na základě URL v MapPage
    topic: null,
    context: null,
    lang: 'cs',
    instanceId: null,
    clientMode: null,
    errors: [],
    externalLayers: [],
    externalLayersCatalog: [],
    isIframe: null,
    layersConfig: undefined,
    currentTools: [],
    mapRef: null,
    writableLayers: [],
    activeTool: null,
    selectedDetailFeature: [],
    activeLayers: [],
    disabled: true,
    layersMode: EditorLayersMode.DATA,
    selectedLayerId: null,
    layerSettingsEnabled: true,
    attributeTableDiscriminatedChildId: null,
    isAttributeTableOpen: false,
    isClientAttributeTableOpen: false,
    clientAttributeTableLayerId: null,
};

export type MapioContextProp = {
    value: MapioContextValue;
    set: Dispatch<SetStateAction<MapioContextValue>>;
    setMapRef: (ref: MutableRefObject<MapEditorActions>) => void;
    setWritableLayers: (layers: LayerId[]) => void;
    setActiveTool: (tool: EditorToolType | null) => void;
    setInsertionLayerId: (layerId: LayerId | undefined) => void;
    setSelectedDetailFeature: (features: GroupFeaturePointer[]) => void;
    setActiveLayers: (layers: LayerId[]) => void;
    setSelectedLayerId: (layerId: LayerId | null) => void;
    setCurrentTools: (tools: (EditorTool | ExtendedEditorToolDefinition)[]) => void;
    setDisabled: (disabled: boolean) => void;
    setLayersMode: (mode: EditorLayersMode) => void;
    setTopicId: (topicId: string) => void;
    setTopic: (topic: TopicVO) => void;
    setLayerSettingsEnabled: (enabled: boolean) => void;
    setAttributeTableDiscriminatedChildId: (id: LayerId | null) => void;
    setIsAttributeTableOpen: (open: boolean) => void;
    setIsClientAttributeTableOpen: (open: boolean) => void;
    openAttributeTable: (layerId: LayerId) => void;
};

export const MapioContext: Context<MapioContextProp> = createContext<MapioContextProp>(undefined);

export const useMapioContext = (): MapioContextProp => {
    const context = useContext(MapioContext);
    if (!context) {
        throw new Error('useMapioContext musí být použit uvnitř MapioContext.Provider');
    }
    return context;
};

export const useMapioContextProvider = (initialValue: Partial<MapioContextValue> = {}) => {
    const [state, setState] = useState<MapioContextValue>({
        ...defaultMapioContextValue,
        ...initialValue,
    });

    const contextValue = useMemo<MapioContextProp>(
        () => ({
            // Pro zpětnou kompatibilitu, ale rád bych přesunul vše
            value: state,
            set: setState,

            // TODO - do budoucna přesunout vše.
            setMapRef: (mapRef: MutableRefObject<MapEditorActions>) => setState(prev => ({...prev, mapRef})),
            setWritableLayers: (writableLayers: LayerId[]) => setState(prev => ({...prev, writableLayers})),
            setActiveTool: (activeTool: EditorToolType | null) => setState(prev => ({...prev, activeTool})),
            setInsertionLayerId: (insertionLayerId: LayerId | undefined) => setState(prev => ({...prev, insertionLayerId})),
            setSelectedDetailFeature: (selectedDetailFeature: GroupFeaturePointer[]) => setState(prev => ({...prev, selectedDetailFeature})),
            setActiveLayers: (activeLayers: LayerId[]) => setState(prev => ({...prev, activeLayers})),
            setSelectedLayerId: (selectedLayerId: LayerId | null) => setState(prev => ({...prev, selectedLayerId})),
            setCurrentTools: (currentTools: (EditorTool | ExtendedEditorToolDefinition)[]) => setState(prev => ({...prev, currentTools})),
            setDisabled: (disabled: boolean) => setState(prev => ({...prev, disabled})),
            setLayersMode: (layersMode: EditorLayersMode) => setState(prev => ({...prev, layersMode})),
            setTopicId: (topicId: string) => setState(prev => ({...prev, topicId})),
            setTopic: (topic: TopicVO) => setState(prev => ({...prev, topic})),
            setLayerSettingsEnabled: (layerSettingsEnabled: boolean) => setState(prev => ({...prev, layerSettingsEnabled})),
            setAttributeTableDiscriminatedChildId: (attributeTableDiscriminatedChildId: LayerId | null) =>
                setState(prev => ({...prev, attributeTableDiscriminatedChildId})),
            setIsAttributeTableOpen: (isAttributeTableOpen: boolean) => setState(prev => ({...prev, isAttributeTableOpen})),
            setIsClientAttributeTableOpen: (isClientAttributeTableOpen: boolean) =>
                setState(prev => ({
                    ...prev,
                    isClientAttributeTableOpen,
                    clientAttributeTableLayerId: isClientAttributeTableOpen ? prev.clientAttributeTableLayerId : null,
                })),
            openAttributeTable: (layerId: LayerId) =>
                setState(prev => {
                    // Externí (připojené / exportované) vrstvy mají data klientsky → otevíráme oddělenou klientskou tabulku.
                    const isExternal = prev.externalLayers?.some(layer => String(layer.id) === String(layerId));
                    if (isExternal) {
                        return {
                            ...prev,
                            isAttributeTableOpen: false,
                            isClientAttributeTableOpen: !!layerId,
                            clientAttributeTableLayerId: layerId,
                        };
                    }
                    const parentId = prev.layersConfig ? getParentLayerId(prev.layersConfig, layerId) : null;
                    return {
                        ...prev,
                        selectedLayerId: parentId ?? layerId,
                        attributeTableDiscriminatedChildId: parentId ? layerId : null,
                        isAttributeTableOpen: !!layerId,
                        isClientAttributeTableOpen: false,
                        clientAttributeTableLayerId: null,
                    };
                }),
        }),
        [state],
    );

    return contextValue;
};
